import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ApiError, Avatar, Badge, Spinner, Stars, api, rideStatusLabel } from '@mobi/web';

interface TrackData {
  status: string;
  origin: { address: string };
  destination: { address: string };
  driver: {
    name: string;
    rating: number | null;
    vehicle: { plate: string; model: string; color: string } | null;
  } | null;
}

/** Cor do estado, para quem só vai olhar essa tela uma vez saber ler de longe. */
const STATUS_TONE: Record<string, 'brand' | 'good' | 'warn' | 'neutral'> = {
  requested: 'warn',
  offering: 'warn',
  accepted: 'brand',
  driver_arrived: 'brand',
  in_progress: 'good',
  finished: 'neutral',
  cancelled: 'neutral',
  no_drivers: 'neutral',
};

/**
 * Acompanhamento público, para quem recebeu o link.
 *
 * Sem login e sem telefone: mostra só o suficiente para alguém saber que a
 * pessoa está a caminho. O link morre junto com a corrida.
 *
 * É a única tela do produto que alguém sem conta pode abrir — quem clica
 * pode nunca ter visto o app. Por isso carrega a identidade da marca
 * mais do que qualquer tela interna: pode ser a primeira impressão.
 */
export function Track() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<TrackData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    let alive = true;

    const load = async () => {
      try {
        const res = await api<TrackData>(`/api/rides/track/${token}`, { skipRefresh: true });
        if (alive) {
          setData(res);
          setError(null);
        }
      } catch (err) {
        if (!alive) return;
        setError(
          err instanceof ApiError && err.status === 410
            ? 'Esta viagem já terminou.'
            : 'Link inválido ou expirado.',
        );
      }
    };

    void load();
    // Sem WebSocket aqui: quem acompanha não tem sessão. Uma consulta a
    // cada 10s basta para "está chegando".
    const timer = setInterval(load, 10_000);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [token]);

  return (
    <div className="h-app overflow-y-auto bg-surface">
      {/* Faixa de marca no topo — o mesmo gradiente do splash e dos
          botões primários, para quem nunca abriu o app reconhecer que
          isto é WiGo desde o primeiro segundo. Hex fixo, não var(): esta
          tela carrega sem sessão e sem app instalado, então não vale
          apostar que o CSS da marca já terminou de chegar. */}
      <div className="bg-[linear-gradient(165deg,#ff8f3d,#ff5a1f_55%,#c2360a)] px-5 pb-14 pt-10">
        {/* O símbolo, não a palavra escrita: a wordmark é toda laranja,
            e sobre um fundo também laranja o texto quase some. O pino
            tem preto e branco no desenho — é o que sobrevive aqui. */}
        <img src="/brand-pin.png?v=2" alt="WiGo" className="mx-auto h-16 w-auto" />
      </div>

      <div className="mx-auto -mt-8 w-full max-w-md px-5 pb-12">
        {!data && !error && (
          <div className="elevated-lg grid place-items-center rounded-3xl border border-line bg-surface p-10 text-brand">
            <Spinner className="h-8 w-8" />
          </div>
        )}

        {error && (
          <div className="elevated-lg rounded-3xl border border-line bg-surface p-6 text-center text-muted">
            {error}
          </div>
        )}

        {data && (
          <div className="elevated-lg overflow-hidden rounded-3xl border border-line bg-surface">
            <div className="p-6">
              <div className="flex items-center justify-between gap-3">
                <p className="eyebrow text-faint">Acompanhando a viagem</p>
                <Badge tone={STATUS_TONE[data.status] ?? 'neutral'}>
                  {rideStatusLabel(data.status)}
                </Badge>
              </div>
              <h1 className="display mt-2 text-2xl">{rideStatusLabel(data.status)}</h1>

              <div className="mt-6 space-y-4">
                <div className="flex gap-3">
                  <span className="mt-1.5 h-3 w-3 shrink-0 rounded-full bg-ink" />
                  <div className="min-w-0">
                    <p className="text-xs font-medium uppercase tracking-wide text-faint">
                      Embarque
                    </p>
                    <p className="font-medium">{data.origin.address}</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <span className="mt-1.5 h-3 w-3 shrink-0 rounded-full bg-brand" />
                  <div className="min-w-0">
                    <p className="text-xs font-medium uppercase tracking-wide text-faint">
                      Destino
                    </p>
                    <p className="font-medium">{data.destination.address}</p>
                  </div>
                </div>
              </div>

              {data.driver && (
                <div className="mt-6 flex items-center gap-4 rounded-2xl bg-surface-2 p-4">
                  {/* Sem foto de propósito: quem acompanha por este link
                      não tem conta, e a rota de foto do motorista exige
                      sessão — expor o rosto dele aqui, sem autenticação,
                      seria abrir a mesma imagem para qualquer um com o
                      link. As iniciais já dizem "é uma pessoa", que é o
                      que esta tela precisa. */}
                  <Avatar name={data.driver.name} size="md" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-semibold">{data.driver.name}</p>
                    <Stars value={data.driver.rating} />
                    {data.driver.vehicle && (
                      <p className="tabular mt-1 text-sm text-muted">
                        <span className="rounded-md bg-surface-3 px-1.5 py-0.5 font-mono text-xs font-bold tracking-wider text-ink">
                          {data.driver.vehicle.plate}
                        </span>{' '}
                        {data.driver.vehicle.model} · {data.driver.vehicle.color}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            <p className="border-t border-line bg-surface-2/60 py-3 text-center text-xs text-faint">
              Atualiza sozinho a cada 10 segundos.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
